import { useEffect, useState } from "react";
import { Loader2, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTargetConnection, useUpdateTargetConnection } from "@/hooks/useApi";
import { useToast } from "@/hooks/use-toast";
import { useConfigurationLogin, useSaveConfigurationLogin } from "@/hooks/useConfigMigration";
import { errorDetailOr } from "@/lib/api-error";
import { connectionSaveFailureText } from "@/lib/connection-save-failure";

const CLIENT_ROLE = "configuration" as const;
/** O tenant GROW Configuration é sempre o client 100. */
const CONFIGURATION_CLIENT = "100";

/** Sub-aba Configuration (cliente 100) do formulário GROW Connection.
 *
 * URL e client são do projeto; o login no tenant é de quem está logado: o checkbox
 * "Usar o mesmo usuário e senha do CBC" (marcado por padrão) ou um usuário e senha
 * próprios. Não há teste de conexão (D5 da spec 2026-10-26-cbc-por-usuario): um Save
 * grava as duas coisas. */
export function ConfigurationTenantForm({
  projectId,
  requireUrlAndClient = false,
}: {
  projectId: string;
  /**
   * Execução (etapa 3): URL e client são obrigatórios; sem os dois o Save não grava. O
   * client vazio já abre com 100, e a falha do Save aparece no próprio formulário (o
   * modal da etapa é a única saída da tela).
   */
  requireUrlAndClient?: boolean;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const {
    data,
    isError: loadFailed,
    error: loadError,
  } = useTargetConnection(projectId, CLIENT_ROLE);
  const update = useUpdateTargetConnection(projectId, CLIENT_ROLE);

  const [baseUrl, setBaseUrl] = useState("");
  const [client, setClient] = useState("");
  const login = useConfigurationLogin(projectId);
  const saveLogin = useSaveConfigurationLogin(projectId);
  const [useCbcCredentials, setUseCbcCredentials] = useState(true);
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setBaseUrl(data?.base_url ?? "");
    const saved = data?.client ?? "";
    setClient(requireUrlAndClient && saved.trim() === "" ? CONFIGURATION_CLIENT : saved);
  }, [data?.base_url, data?.client, requireUrlAndClient]);

  useEffect(() => {
    // O estado do login só vem de uma resposta do GET. Sem resposta (carregando ou com erro) o
    // form fica travado; o padrão (checkbox marcado, D3, D10) vale quando o GET devolve dado.
    if (!login.data) return;
    setUseCbcCredentials(login.data.use_cbc_credentials ?? true);
    setLoginUsername(login.data.username ?? "");
  }, [login.data, login.data?.use_cbc_credentials, login.data?.username]);

  // Sem `login.data` o checkbox e o Save ficam travados: um clique antes da resposta seria
  // sobrescrito pelo useEffect, e um Save sem ela mandaria `use_cbc_credentials: true` e
  // apagaria o login guardado. No React Query v5 `isLoading` fica false quando o GET erra, por
  // isso a trava olha o dado e não o estado de carregamento.
  const loginLoading = !login.data;
  const hasStoredPassword = login.data?.has_password === true;
  const missingLogin =
    !useCbcCredentials &&
    (loginUsername.trim() === "" || (loginPassword === "" && !hasStoredPassword));

  const missingRequired = requireUrlAndClient && (baseUrl.trim() === "" || client.trim() === "");

  const handleSave = async () => {
    if (missingRequired || missingLogin) return;
    setSaving(true);
    setSaveError(null);
    // O login vai primeiro: é a gravação de URL e client que libera o gate da Execução (a releitura
    // desmonta este form), e um login que falhasse depois dela cairia num componente desmontado.
    let loginSaved = false;
    try {
      await saveLogin.mutateAsync(
        useCbcCredentials
          ? { use_cbc_credentials: true }
          : {
              use_cbc_credentials: false,
              username: loginUsername.trim(),
              ...(loginPassword !== "" ? { password: loginPassword } : {}),
            },
      );
      loginSaved = true;
      await update.mutateAsync({
        base_url: baseUrl.trim() || undefined,
        client: client.trim() || undefined,
      });
      setLoginPassword("");
      toast({ title: t("Configuration tenant saved") });
    } catch (err) {
      const reason = connectionSaveFailureText(t, err, "");
      // Login salvo e URL/client não: a pessoa precisa saber o que ficou gravado.
      const partial = loginSaved ? t("Your login was saved, but the URL and client were not.") : "";
      const detail = [partial, reason].filter(Boolean).join(" ");
      if (requireUrlAndClient) {
        setSaveError(detail || t("Could not save Configuration tenant"));
      } else {
        toast({
          title: t("Could not save Configuration tenant"),
          description: detail || undefined,
          variant: "destructive",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const loadDetail = loadFailed ? errorDetailOr(loadError, "") : "";

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t(
          "Client 100 tenant where the SSCUIs are maintained. The View button of each SSCUI opens the configuration screen here, in your own browser session.",
        )}
      </p>
      {loadFailed && (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <XCircle className="w-4 h-4 shrink-0" />
          <span>{t("Could not load Configuration tenant")}</span>
          {loadDetail && <span className="text-muted-foreground">{loadDetail}</span>}
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="configuration-base-url">{t("URL")}</Label>
        <Input
          id="configuration-base-url"
          placeholder="https://myNNNNNN-api.s4hana.cloud.sap"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="configuration-client">{t("Client")}</Label>
        <Input
          id="configuration-client"
          placeholder="100"
          value={client}
          onChange={(e) => setClient(e.target.value)}
        />
      </div>
      {login.isError && !login.data && (
        <div role="alert" className="flex items-center gap-2 text-sm text-destructive">
          <XCircle className="w-4 h-4 shrink-0" />
          <span>{t("Could not load your Configuration login")}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={login.isFetching}
            onClick={() => void login.refetch()}
          >
            {t("Retry")}
          </Button>
        </div>
      )}
      <div className="flex items-center gap-3">
        <Checkbox
          id="configuration-use-cbc-credentials"
          checked={useCbcCredentials}
          disabled={loginLoading}
          onCheckedChange={(value) => setUseCbcCredentials(value === true)}
        />
        <Label htmlFor="configuration-use-cbc-credentials" className="cursor-pointer leading-none">
          {t("Use the same CBC user and password")}
        </Label>
      </div>
      {!useCbcCredentials && (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="configuration-username">{t("Configuration user")}</Label>
            <Input
              id="configuration-username"
              value={loginUsername}
              onChange={(e) => setLoginUsername(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="configuration-password">{t("Configuration password")}</Label>
            <Input
              id="configuration-password"
              type="password"
              placeholder={hasStoredPassword ? "••••••••" : ""}
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
            />
            {hasStoredPassword && (
              <p className="text-xs text-muted-foreground">
                {t("Leave it blank to keep the saved password.")}
              </p>
            )}
          </div>
        </>
      )}
      {saveError && (
        <div role="alert" className="flex items-center gap-2 text-sm text-destructive">
          <XCircle className="w-4 h-4 shrink-0" />
          <span>{saveError}</span>
        </div>
      )}
      <div className="flex items-center gap-3 pt-2">
        <Button onClick={handleSave} disabled={saving || missingRequired || missingLogin || loginLoading}>
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {t("Save")}
        </Button>
      </div>
    </div>
  );
}
